/**
 * C1 v1.50 (c1-overrides, go-live unit 16, CL-040): the price overrides of C2 v1.52. An override is an Amazon marketplace
 * price committed with a reason and an until date (the New price box's two fields; C2 refuses a store row). It is in force
 * while it is the newest price on its row; on the day after its until date the daily return call writes the price in force
 * before it back (`override_return`), or a person ends it earlier with End now. A later ordinary commit replaces it with no
 * return. Types mirror C2's JSON field for field; every price, date, count and `days_left` is C2's string or integer, nothing
 * is computed here, and a return is applied by hand like every commit until C6.
 */
import type { QueryClient } from "@tanstack/react-query";

import { call, PricingError, type Market } from "./api";
import { COPY, humanDate, money, personName } from "./format";

export type OverrideStatus = "in_force" | "due" | "returned" | "replaced";
/** One override as `GET /api/overrides` lists it (C2 v1.52 `_item`). `return_price` is null on a row that had no committed price;
 * `days_left` is set in force only; the `end_*` fields name the entry that ended it (a return or a later commit). */
export type Override = {
  entry_id: number; xero_sku: string; product: string; variant: string; market: Market; channel: string; channel_location: string;
  marketplace_id: string; marketplace_name: string | null; price: string; return_price: string | null; currency: string;
  reason: string; until: string; approver: string; committed_at: string; status: OverrideStatus; days_left: number | null;
  end_entry_id: number | null; ended_by: string | null; ended_at: string | null;
};
export type OverridesRead = { overrides: Override[]; today: string; counts: Record<OverrideStatus, number> };
/** End now's answer: the return entry written, the override as it now reads, and the RRP the return set when the row is
 * the region's anchor (C2 v1.52 call B, review A2), null otherwise. */
export type EndAnswer = { return_entry_id: number; override: Override; rrp: { rrp: string; currency: string; previous_rrp: string | null } | null };

export const STATUSES: OverrideStatus[] = ["in_force", "due", "returned", "replaced"];
export const OVERRIDES_KEY = ["pricing", "overrides"] as const;
export const getOverrides = () => call<OverridesRead>("/overrides");
/** End now: `POST /api/overrides/{entry_id}/end`, a person with edit on the module. */
export const endOverride = (entryId: number) => call<EndAnswer>(`/overrides/${entryId}/end`, { method: "POST" });
/** Never stale: a commit or a return elsewhere must show on the next focus, as the price runs do. */
export function overridesQuery() {
  return { queryKey: OVERRIDES_KEY, queryFn: getOverrides, retry: 1, staleTime: 0 };
}
/** After End now, and after any commit: the overrides move with the Price Log. */
export function refreshOverrides(client: QueryClient): void {
  void client.invalidateQueries({ queryKey: OVERRIDES_KEY });
}

/** The override fields of a stage body: both trimmed, neither when both are empty, null when only one is given (C2 would refuse it
 * with 422 bad_override, so the box says so and sends nothing). The same shape as the RRP exception's `exceptionFields`. */
export function overrideFields(reason: string, until: string): { override_reason?: string; override_until?: string } | null {
  const why = reason.trim();
  const when = until.trim();
  if (!why && !when) return {};
  if (!why || !when) return null;
  return { override_reason: why, override_until: when };
}
/** Only an Amazon marketplace row takes an override (C2 v1.52 review A1): the box offers the fields there alone. */
export const offersOverride = (row: { channel: string }): boolean => row.channel === "amazon";

/** The override standing on a row's committed price: the newest in force or due one for its SKU and marketplace, else null. An
 * ended override is history (the list shows it; the row does not). */
export function overrideFor(read: OverridesRead | undefined, sku: string, place: string): Override | null {
  return read?.overrides.find((o) => o.xero_sku === sku && o.marketplace_id === place && (o.status === "in_force" || o.status === "due")) ?? null;
}

/** The chip on the Price Run row and the drawer: "Override · 10 days left", "Override · last day", or once the date passed
 * "Override ended 14 Oct: returns the next morning". `days_left` is C2's integer; the words only choose a form for it. */
export function overrideChipText(o: Override): string {
  // Review round 1, comment 1: a due override with no prior price never returns (C2 skips it); it ends by the next commit.
  if (o.status === "due") return o.return_price === null ? OVERRIDE_COPY.chipDueNoReturn(humanDate(o.until)) : OVERRIDE_COPY.chipDue(humanDate(o.until));
  if (o.days_left === 0) return OVERRIDE_COPY.chipLastDay;
  return OVERRIDE_COPY.chip(o.days_left ?? 0);
}
/** The line under the chip: the reason, and the price it returns to (or that it cannot return). */
export function overrideLineText(o: Override): string {
  return o.return_price === null ? OVERRIDE_COPY.lineNoReturn(o.reason) : OVERRIDE_COPY.line(o.reason, money(o.return_price, o.currency), humanDate(o.until));
}
/** A saved line that carries the pair, under the saved chip (C1 v1.35's reason line pattern). */
export function savedOverrideText(line: { override_reason?: string | null; override_until?: string | null }): string | null {
  return line.override_reason && line.override_until ? OVERRIDE_COPY.savedLine(line.override_reason, humanDate(line.override_until)) : null;
}
/** The list's Status cell: the status word, and who or what ended it. The return call's service account reads as "the daily return". */
export function statusText(o: Override): string {
  if (o.status === "in_force") return o.days_left === 0 ? OVERRIDE_COPY.status.lastDay : OVERRIDE_COPY.status.in_force(o.days_left ?? 0);
  if (o.status === "due") return o.return_price === null ? OVERRIDE_COPY.status.dueNoReturn : OVERRIDE_COPY.status.due;
  if (o.status === "returned") return OVERRIDE_COPY.status.returned(humanDate(o.ended_at), enderName(o.ended_by));
  return OVERRIDE_COPY.status.replaced(humanDate(o.ended_at), enderName(o.ended_by));
}
export function enderName(email: string | null): string {
  return email && email.endsWith(".gserviceaccount.com") ? OVERRIDE_COPY.dailyReturn : personName(email);
}
/** End now is offered on an override in force or due that has a price to return to; C2 refuses the rest (409), and the row says why. */
export const canEnd = (o: Override): boolean => (o.status === "in_force" || o.status === "due") && o.return_price !== null;

/** The apply-by-hand line after End now: the return just written, with the RRP it moved when the row is the anchor. */
export function returnApplyText(answer: EndAnswer): string {
  const o = answer.override;
  const place = o.marketplace_name ?? o.marketplace_id;
  const head = OVERRIDE_COPY.ended(`${o.product} ${o.variant}`, place, money(o.return_price, o.currency), money(o.price, o.currency));
  const rrp = answer.rrp ? ` ${OVERRIDE_COPY.rrpFollowed(money(answer.rrp.rrp, answer.rrp.currency), answer.rrp.previous_rrp === null ? null : money(answer.rrp.previous_rrp, answer.rrp.currency))}` : "";
  return `${head}${rrp}`;
}

/** An API before C2 v1.52 has no overrides route: the read answers 404, which the section names once, with no Retry (review round 1, comment 3). */
export const isOlderApi = (error: unknown): boolean => error instanceof PricingError && error.status === 404;

/** The sentence for a refused End now, in words, never C2's field names. */
export function endRefusal(error: unknown, areaName: string): string {
  if (!(error instanceof PricingError)) return COPY.unreachable;
  const reason = error.reason();
  if (reason === "override_not_in_force") return OVERRIDE_COPY.refusal.notInForce;
  if (reason === "no_prior_price") return OVERRIDE_COPY.refusal.noPrior;
  if (reason === "unknown_override") return OVERRIDE_COPY.refusal.unknown;
  if (error.status === 401 || error.status === 403) return COPY.noEdit(areaName);
  return error.facts().message ?? COPY.unreachable;
}

export const OVERRIDE_COPY = {
  box: {
    toggle: "Temporary price",
    toggleNote: "Set a reason and a date; the price returns to the one in force before it the morning after that date (applied by hand).",
    reasonLabel: "Why this temporary price?",
    reasonPlaceholder: "Reason, such as Prime Day",
    untilLabel: "Until",
    note: "Through the end of that day, UTC. A return is written the next morning at 07:00 Berlin and listed under Price overrides to apply by hand.",
    both: "Give both a reason and an until date for the temporary price, or clear both.",
    save: "Save temporary price",
    recorded: (until: string, back: string | null) => ` Temporary until ${until}${back ? `; returns to ${back} the morning after` : "; this row has no earlier price to return to"}.`,
    amazonOnly: "A temporary price is for an Amazon marketplace row. A store promotion is a discount: set it in Discounts.",
    bad: (day: string | null) => `The temporary price was not saved: give both a reason (at most 500 characters) and an until date of ${day ?? "today"} or later.`,
  },
  chip: (days: number) => `Override · ${days} ${days === 1 ? "day" : "days"} left`,
  chipLastDay: "Override · last day",
  chipDue: (until: string) => `Override ended ${until}: returns the next morning`,
  chipDueNoReturn: (until: string) => `Override ended ${until}: no earlier price, ends by the next commit`,
  line: (reason: string, back: string, until: string) => `${reason} · returns to ${back} after ${until}`,
  lineNoReturn: (reason: string) => `${reason} · no earlier price to return to: ends by the next commit`,
  savedLine: (reason: string, until: string) => `Saved as a temporary price until ${until}: ${reason}`,
  applyNote: (until: string, back: string | null) => (back ? `temporary until ${until}, returns to ${back} the morning after` : `temporary until ${until}, no earlier price to return to`),
  section: {
    title: "Price overrides",
    note: "Temporary Amazon prices with a reason and an until date, every region (the Region buttons filter the rows above, not this list). The morning after the date the return is written here and in the Price log; apply it by hand like every commit. End now writes the return today.",
    counts: (c: Record<OverrideStatus, number>) => `${c.in_force} in force · ${c.due} due · ended in the last 30 days: ${c.returned} returned, ${c.replaced} replaced`,
    empty: "No price override in force. Save a price with Temporary price in a row's New price box to set one.",
    unread: "The price overrides could not be read.",
    olderApi: "The pricing API is older than this screen and does not serve price overrides yet.",
    columns: ["Product", "Where", "Price", "Reason", "Until", "Status", ""],
    returnsTo: (price: string) => `returns to ${price}`,
    noReturn: "no earlier price",
    committedBy: (who: string, when: string) => `${who}, ${when}`,
    end: "End now",
    ending: "Ending…",
    done: "Done",
  },
  status: {
    in_force: (days: number) => `In force · ${days} ${days === 1 ? "day" : "days"} left`,
    lastDay: "In force · last day",
    due: "Due · returns the next morning",
    dueNoReturn: "Due · no earlier price, ends by the next commit",
    returned: (when: string, who: string) => `Returned ${when} by ${who}`,
    replaced: (when: string, who: string) => `Replaced ${when} by ${who}`,
  },
  dailyReturn: "the daily return",
  ended: (product: string, place: string, back: string, was: string) =>
    `Override ended. Apply ${back} on ${place} for ${product} by hand (it was ${was}); the next calculation shows it as the live price.`,
  rrpFollowed: (rrp: string, previous: string | null) => `The RRP now follows it: ${rrp}${previous ? ` (was ${previous})` : ""}.`,
  refusal: {
    notInForce: "Not ended: this override is no longer in force (returned or replaced already). The list reads again.",
    noPrior: "Not ended: this row had no committed price before the override, so there is nothing to return to. It ends with the next commit on the row.",
    unknown: "Not ended: this override is not known. The list reads again.",
  },
} as const;
